"use client";

import { useState } from "react";
import { MousePointerClick, Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { t } from "@/lib/localization";
import { WalkthroughBlock as WalkthroughBlockType } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import WalkthroughEditor from "./walkthroughEditor";
import WalkthroughPlayer from "./walkthroughPlayer";

export default function WalkthroughBlock({
    block,
}: {
    block: WalkthroughBlockType;
}) {
    const [editorOpen, setEditorOpen] = useState(false);
    const steps = block.data.steps;
    const editable = useBlockEditable();

    if (!editable) {
        if (steps.length === 0) return null;
        return (
            <div className="lomda-v3-card-surface h-[60vh] max-h-[40rem] min-h-[26rem] overflow-hidden rounded-2xl border shadow-sm">
                <WalkthroughPlayer steps={steps} className="h-full" />
            </div>
        );
    }

    return (
        <>
            {steps.length === 0 ? (
                <div className="lomda-v3-card-surface flex flex-col items-start gap-4 rounded-2xl border p-8 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-col gap-2">
                        <span className="lomda-v3-text-h6">
                            {t("lomda-v3-editor.walkthrough.get-started")}
                        </span>
                        <span className="max-w-md lomda-v3-text-small text-muted-foreground">
                            {t("lomda-v3-editor.walkthrough.get-started-desc")}
                        </span>
                        <Button
                            onClick={() => setEditorOpen(true)}
                            className="mt-2 w-fit"
                        >
                            {t("lomda-v3-editor.walkthrough.start-creating")}
                        </Button>
                    </div>
                    <span className="flex size-16 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <MousePointerClick className="size-7" />
                    </span>
                </div>
            ) : (
                <div className="lomda-v3-card-surface flex items-center justify-between rounded-2xl border p-5">
                    <div className="flex items-center gap-3">
                        <span className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                            <MousePointerClick className="size-6" />
                        </span>
                        <div className="flex flex-col">
                            <span className="font-semibold">
                                {t("lomda-v3-editor.interactive.walkthrough")}
                            </span>
                            <span className="lomda-v3-text-small text-muted-foreground">
                                {t("lomda-v3-editor.walkthrough.n-steps", undefined, {
                                    n: steps.length,
                                })}
                            </span>
                        </div>
                    </div>
                    <Button variant="outline" onClick={() => setEditorOpen(true)}>
                        <Pencil className="size-4 me-2" />
                        {t("lomda-v3-editor.walkthrough.edit-walkthrough")}
                    </Button>
                </div>
            )}

            {editorOpen && (
                <WalkthroughEditor
                    block={block}
                    onClose={() => setEditorOpen(false)}
                />
            )}
        </>
    );
}
